import { apiFetch } from "../common/api";

const HOST = import.meta.env.VITE_API_HOST;

/**
 * List all campaigns.
 * @returns {Promise<Array>}
 */
export async function listCampaigns() {
  const resp = await apiFetch(`${HOST}/api/v1/campaigns`);
  if (resp.status !== 200) throw await resp.text();
  const data = await resp.json();
  return data.items;
}

/**
 * Get a single campaign.
 * @param {number} id
 * @returns {Promise<object>}
 */
export async function getCampaign(id) {
  const resp = await apiFetch(`${HOST}/api/v1/campaigns/${id}`);
  if (resp.status !== 200) throw await resp.text();
  return resp.json();
}

/**
 * Create a new campaign.
 * @param {object} data — { trigger_type, config, name? }
 * @returns {Promise<object>}
 */
export async function createCampaign(data) {
  const resp = await apiFetch(`${HOST}/api/v1/campaigns`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (resp.status !== 200) throw await resp.text();
  return resp.json();
}

/**
 * Update a campaign.
 * @param {number} id
 * @param {object} data — { config, enabled?, name? }
 * @returns {Promise<object>}
 */
export async function updateCampaign(id, data) {
  const resp = await apiFetch(`${HOST}/api/v1/campaigns/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (resp.status !== 200) throw await resp.text();
  return resp.json();
}

/**
 * Delete a campaign.
 * @param {number} id
 */
export async function deleteCampaign(id) {
  const resp = await apiFetch(`${HOST}/api/v1/campaigns/${id}`, {
    method: "DELETE",
  });
  if (resp.status !== 200) throw await resp.text();
}

/**
 * Manually run a campaign.
 * @param {number} id
 * @returns {Promise<object>}
 */
export async function runCampaign(id) {
  const resp = await apiFetch(`${HOST}/api/v1/campaigns/${id}/run`, {
    method: "POST",
  });
  if (resp.status !== 200) throw await resp.text();
  return resp.json();
}

/**
 * Get notification log for a campaign.
 * @param {number} id
 * @returns {Promise<object>}
 */
export async function getCampaignLog(id) {
  const resp = await apiFetch(`${HOST}/api/v1/campaigns/${id}/log`);
  if (resp.status !== 200) throw await resp.text();
  return resp.json();
}

/**
 * List available trigger types with config schemas.
 * @returns {Promise<Array>}
 */
export async function getTriggerTypes() {
  const resp = await apiFetch(`${HOST}/api/v1/campaigns/trigger-types`);
  if (resp.status !== 200) throw await resp.text();
  const data = await resp.json();
  return data.items;
}

/**
 * Send a test push for a campaign to a customer by phone number.
 * @param {number} id — campaign ID
 * @param {string} phone — customer phone number
 * @returns {Promise<object>} — { title, body, customer_name }
 */
export async function testCampaign(id, phone) {
  const resp = await apiFetch(`${HOST}/api/v1/campaigns/${id}/test`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ phone }),
  });
  if (resp.status !== 200) throw await resp.text();
  return resp.json();
}

/**
 * Get campaign stats (candidates, sent, failed).
 * @param {number} id — campaign ID
 * @returns {Promise<{candidates: number, sent: number, failed: number}>}
 */
export async function getCampaignStats(id) {
  const resp = await apiFetch(`${HOST}/api/v1/campaigns/${id}/stats`);
  if (resp.status !== 200) throw await resp.text();
  return resp.json();
}

/**
 * Get the SQL query for a campaign that analytics can run.
 * @param {number} id — campaign ID
 * @returns {Promise<{sql: string}>}
 */
export async function getCampaignSql(id) {
  const resp = await apiFetch(`${HOST}/api/v1/campaigns/${id}/sql`);
  if (resp.status !== 200) throw await resp.text();
  return resp.json();
}
